<template lang="html">
  <div class="cw-order-cell box is-floating" @click="$emit('click')">
    <cw-basic-cell class="has-border-bottom">
      <div slot="left-area">
        <icon :name="orderTypeIcon.name" :scale="orderTypeIcon.scale"></icon>
      </div>
      <div slot="left-fill-area">
        <!-- <p class="is-size-6 has-text-grey-medium">type</p> -->
        <p class="is-size-5">{{ tripType }}</p>
        <p class="is-size-6 has-text-grey-medium">
          Work Order {{ boxSetInfo.workOrderId }}
        </p>
        <p v-if="isOptimisticEntry" class="is-size-7 has-text-primary">
          {{ isDelayedEntry ? 'Still confirming…' : 'Confirming…' }}
        </p>
        <p
          v-if="canViewOrderedBy && orderedByName"
          class="is-size-7 has-text-grey-medium"
        >
          Ordered by {{ orderedByName }}
        </p>
      </div>
      <div slot="right-area-1">
        <div
          v-if="boxSetInfo.status && boxSetInfo.status === 'POHold'"
          class="has-text-centered"
        >
          <a
            class="button is-warning is-rounded is-medium"
            name="button"
            @click="showPOWarning"
          >
            <span class="icon">
              <i class="las la-2x la-exclamation-circle"></i>
            </span>
            <span>PO Needed</span>
          </a>
        </div>
        <div v-else>
          <p class="is-size-5 right-align">
            {{ dispatchPull.dateNeededBy || boxSetInfo.dateNeededBy }}
          </p>
          <p class="is-size-6 has-text-grey-medium right-align">Date Needed</p>
        </div>
        <p
          v-if="displayDateCreated"
          class="is-size-6 has-text-grey-medium right-align"
        >
          Created {{ displayDateCreated }}
        </p>
      </div>
    </cw-basic-cell>

    <cw-basic-cell>
      <div slot="left-area">
        <span :class="[boxSizeIcon.colorClass, boxSizeIcon.scale]">
          {{ boxSizeIcon.label }}
        </span>
      </div>
      <div slot="left-fill-area">
        <p class="is-size-5">Lot {{ subProject.name }}</p>
        <p class="is-size-6 has-text-grey-medium">
          {{ boxSetInfo.intendedUse }}
        </p>
      </div>
      <div slot="right-area-2">
        <p class="is-size-5 right-align">
          {{ (boxSetInfo.box && boxSetInfo.box.boxNumber) || '' }}
        </p>
        <p class="is-size-6 has-text-grey-medium right-align">
          {{
            (boxSetInfo.box && boxSetInfo.box.boxNumber && 'Box Number') || ''
          }}
        </p>
      </div>
    </cw-basic-cell>
  </div>
</template>

<script>
import PermissionTypes from '@/enums/permissions';

export default {
  name: 'cw-order-cell',
  props: {
    boxSetInfo: {
      type: Object,
      required: true,
    },
  },
  computed: {
    // Mirrors the server's gate on DispatchSet.orderedByName. The role mapping
    // lives on the server (/auth/permissions), so read the permission rather
    // than hard-coding a role here.
    canViewOrderedBy() {
      const permissions = this.$store.state.user.permissions;
      return (
        Array.isArray(permissions) &&
        permissions.includes(PermissionTypes.viewOrderedBy)
      );
    },
    // Optimistic entries carry a synthetic _optimisticKey (see
    // store/modules/orders.js) that real orders never have. Once a service
    // order is confirmed staged in FileMaker (_isStaged), the "did this even
    // go through" uncertainty is resolved, so stop showing the badge even
    // though the placeholder itself hasn't been replaced by the real
    // Pending record yet - that's a separate, slower dispatch-review step.
    isOptimisticEntry() {
      return !!(
        this.boxSetInfo &&
        this.boxSetInfo._optimisticKey &&
        !this.boxSetInfo._isStaged
      );
    },
    isDelayedEntry() {
      return !!(this.boxSetInfo && this.boxSetInfo._isDelayed);
    },
    orderedByName() {
      // For pull-type orders the dispatch pull's contact is authoritative;
      // for sets the dispatch set's contact is. Fall back to the staged
      // order's user for orders with no contact recorded. Only admins
      // receive either value (see the server's viewOrderedBy gate).
      const stagedOrderName =
        (this.boxSetInfo.stagedOrder && this.boxSetInfo.stagedOrder.userName) ||
        '';
      return (
        (this.isPullOrder && this.dispatchPull.orderedByName) ||
        this.boxSetInfo.orderedByName ||
        stagedOrderName
      );
    },
    // FileMaker returns RecordCreated as a full timestamp (e.g.
    // 04/04/2024 21:37:21). Trim to the date part for display, matching
    // how other date fields render in this cell.
    displayDateCreated() {
      const created = this.boxSetInfo && this.boxSetInfo.dateCreated;
      return (created && `${created}`.split(' ')[0]) || '';
    },
    subProject() {
      return (
        this.boxSetInfo.subProject || {
          name: '',
        }
      );
    },
    dispatchPull() {
      return (
        this.boxSetInfo.dispatchPull || {
          tripType: 'Pull',
          dateNeededBy: '',
        }
      );
    },
    // The dispatch pull record is the authoritative source for pull-type
    // orders (Pull, Roundtrip, Respot); the dispatch set's contact only
    // applies to Sets.
    isPullOrder() {
      return !!(
        this.boxSetInfo &&
        this.boxSetInfo.dispatchPull &&
        this.boxSetInfo.dispatchPull.tripType
      );
    },
    tripType() {
      let boxSetInfo = this.boxSetInfo;
      if (boxSetInfo) {
        let type = boxSetInfo.tripType;
        type =
          (boxSetInfo.dispatchPull && boxSetInfo.dispatchPull.tripType) || type;

        return type;
      }
      return 'Set';
    },
    tripTypeIconName() {
      return this.tripType.toLowerCase();
    },
    orderTypeIcon() {
      return {
        name: this.tripTypeIconName,
        scale: 4,
        label: this.tripTypeIconName,
      };
    },
    boxSizeString() {
      // Duplicate in BoxCell.vue
      let setBoxSize =
        this.boxSetInfo && this.boxSetInfo.box && this.boxSetInfo.box.boxSize;
      let orderedBoxSize = this.boxSetInfo && this.boxSetInfo.boxSize;

      let displayedSize = setBoxSize || orderedBoxSize;

      return `${displayedSize} ${(displayedSize !== 'washout' && 'yd') || ''}`;
    },
    boxSizeIcon() {
      return {
        scale: 'is-medium',
        label: this.boxSizeString,
        colorClass: 'tag is-steel',
      };
    },
  },
  methods: {
    showPOWarning() {
      // see duplicate warning in DetailsOrder
      this.$modal.show('alert', {
        title: 'PO Needed',
        subtitle: 'Contact Dispatch',
        message:
          'A purchase order number is required before this order can be fulfilled. Contact dispatch to specify the PO.',
        type: 'warning',
      });
    },
  },
};
</script>

<style lang="stylus" scoped>

p {
  // some overriding of the bulma is-size classes
  $scaling-factor = 0.9
  $phone-width = 480px

  &.is-size-5 {
    font-size: 1.25rem
    @media (max-width: $phone-width) {
      font-size: @font-size * $scaling-factor
    }
  }
  &.is-size-6 {
    font-size: 1rem
    @media (max-width: $phone-width) {
      font-size: @font-size * $scaling-factor
    }
  }
  &.right-align {
    text-align: right
  }
}

.cw-order-cell {
  padding: 0.75em 1.5em
  .cw-list-cell:first-of-type {
    &.level {
      padding-bottom: 1em
    }
  }
  .cw-list-cell:last-of-type{
    margin-top: 1em;
    &.level {
      border-bottom: 0
    }
  }
}
</style>
